// Support Center page: a topic picker that filters the answer list in place.
//
// The answers are authored as static markup in app/index.html — each <details class="sup-faq"> carries
// a space-separated data-cat of the topics it belongs to, so one answer can surface under several
// (e.g. "popular billing"). "popular" is a cross-cutting tag, not a section, which is what lets the
// page open on a useful default instead of an empty state.
//
// Deliberately no routing: the whole app is one page whose views are shown/hidden (see ui/sidenav.ts),
// so the topic lives in the picker's own state. Nothing here talks to the API.

// The CRO Zone Support answers sit under the "playbook" (Cohort Diagnostic) and "team"
// (Teams/Trends/Compare/History) topics. They are visible to everyone — the paid-plan gate lives on the
// CRO pages themselves (main.ts), not on the help content.

/** Wire the topic picker. Safe to call when the Support view isn't the active one. */
export function wireSupport(): void {
  const select = document.getElementById('sup-cat') as HTMLSelectElement | null;
  const count = document.getElementById('sup-count');
  const heading = document.getElementById('sup-res-h');
  if (!select || !count || !heading) return;

  const items = Array.from(document.querySelectorAll<HTMLDetailsElement>('.sup-faq'));
  if (!items.length) return;

  const apply = (topic: string): void => {
    let shown = 0;
    // "all" is the default view: show every answer, in document order. Any other value filters by data-cat.
    const all = topic === 'all';
    for (const item of items) {
      const match = all || (item.dataset.cat ?? '').split(' ').includes(topic);
      item.hidden = !match;
      // Collapse on switch, so a topic always opens in the same scannable state.
      item.open = false;
      if (match) shown++;
    }
    heading.textContent = all ? 'All answers' : (select.options[select.selectedIndex]?.text ?? '');
    const noun = shown === 1 ? 'answer' : 'answers';
    count.textContent = all ? `${shown} ${noun}` : `${shown} ${noun} in this topic`;
  };

  select.addEventListener('change', () => apply(select.value));
  apply(select.value);

  wireEmailCta();
}

// The "Email support" button is a mailto: link, which silently does nothing for anyone without an OS
// mail handler configured (most webmail users). So on click we also copy the address to the clipboard
// with a visible confirmation — and keep the mailto so a real mail client still opens if there is one.
const SUPPORT_EMAIL = 'info@projectmoneyball.com';
function wireEmailCta(): void {
  const cta = document.querySelector<HTMLAnchorElement>('.sup-cta');
  if (!cta) return;
  const label = cta.textContent ?? 'Email support';
  let revert: number | undefined;
  cta.addEventListener('click', () => {
    // Don't preventDefault: the mailto still fires for anyone who has a mail client.
    // Best-effort copy to the clipboard (works in secure contexts under a real click)…
    try {
      void navigator.clipboard?.writeText(SUPPORT_EMAIL);
    } catch {
      /* clipboard unavailable */
    }
    // …and surface the address in the button regardless, so a webmail user with no mail handler and no
    // clipboard access can still read and use it. The address showing IS the useful outcome.
    cta.textContent = `Copied, ${SUPPORT_EMAIL}`;
    cta.classList.add('copied');
    window.clearTimeout(revert);
    revert = window.setTimeout(() => {
      cta.textContent = label;
      cta.classList.remove('copied');
    }, 2600);
  });
}
